import { createServerFn } from "@tanstack/react-start";

export type UnsubscribeResult =
  | { ok: true }
  | { ok: false; reason: "invalid_token" | "missing_params" };

export const performUnsubscribeFn = createServerFn({ method: "POST" })
  .inputValidator(
    (data: { email: string; token: string; bid?: string }) => data,
  )
  .handler(async ({ data }): Promise<UnsubscribeResult> => {
    const email = (data.email ?? "").trim();
    const token = (data.token ?? "").trim();
    const bid = (data.bid ?? "").trim() || undefined;
    if (!email || !token) return { ok: false, reason: "missing_params" };

    const { verifyUnsubscribeToken } = await import(
      "@/lib/email/broadcast-template.server"
    );
    const normalised = email.toLowerCase();

    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );

    // Two valid unsubscribe-token systems coexist:
    //   1) HMAC-signed tokens embedded in broadcast footers
    //      (verifyUnsubscribeToken — deterministic per email).
    //   2) Random per-recipient tokens stored in email_unsubscribe_tokens
    //      and injected into transactional emails by the Lovable email API.
    // A single /unsubscribe endpoint must honour both, otherwise transactional
    // recipients get "invalid link" and only broadcast recipients can opt out.
    let valid = verifyUnsubscribeToken(normalised, token);
    if (!valid && token.length >= 8) {
      const { data: tokRow } = await supabaseAdmin
        .from("email_unsubscribe_tokens")
        .select("email")
        .eq("token", token)
        .maybeSingle();
      if (
        tokRow &&
        ((tokRow as { email: string }).email ?? "").toLowerCase() === normalised
      ) {
        valid = true;
      }
    }
    if (!valid) return { ok: false, reason: "invalid_token" };

    const now = new Date().toISOString();

    await supabaseAdmin
      .from("email_subscribers")
      .upsert(
        {
          email: normalised,
          source: "unsubscribe",
          marketing_consent: false,
          unsubscribed_at: now,
        },
        { onConflict: "email" },
      );

    await supabaseAdmin
      .from("suppressed_emails")
      .upsert(
        { email: normalised, reason: "unsubscribe" },
        { onConflict: "email" },
      );

    const { data: authUsers } = await supabaseAdmin.auth.admin.listUsers({
      page: 1,
      perPage: 1000,
    });
    const user = (authUsers?.users ?? []).find(
      (u) => (u.email ?? "").toLowerCase() === normalised,
    );
    if (user) {
      await supabaseAdmin.from("email_preferences").upsert(
        {
          user_id: user.id,
          product_updates: false,
          membership_reminders: false,
          new_features: false,
          events_webinars: false,
          newsletter: false,
          therapist_updates: false,
          marketing_promotional: false,
          updated_at: now,
        },
        { onConflict: "user_id" },
      );
    }

    if (bid) {
      const { data: rec } = await supabaseAdmin
        .from("email_broadcast_recipients")
        .select("id, unsubscribed_at")
        .eq("broadcast_id", bid)
        .eq("email", normalised)
        .maybeSingle();
      if (rec && !rec.unsubscribed_at) {
        await supabaseAdmin
          .from("email_broadcast_recipients")
          .update({ status: "unsubscribed", unsubscribed_at: now })
          .eq("id", rec.id);
        const { data: bc } = await supabaseAdmin
          .from("email_broadcasts")
          .select("unsubscribe_count")
          .eq("id", bid)
          .maybeSingle();
        await supabaseAdmin
          .from("email_broadcasts")
          .update({ unsubscribe_count: (bc?.unsubscribe_count ?? 0) + 1 })
          .eq("id", bid);
      }
    }
    return { ok: true };
  });
